const API_BASE = 'https://services.netsights.ai';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

async function apiPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────
export interface TicketData {
  ticket_id: number;
  ticket_number: string;
  category: string;
  zoho_synced: boolean;
}

export interface AskResponse {
  answer: string | null;
  source: 'kb' | 'qa' | null;
  confidence: 'high' | 'medium' | 'low' | null;
  needs_ticket: boolean;
  message?: string;
  suggestions?: Array<{
    type: string;
    id: number;
    title: string;
    category?: string;
    option_key?: string;
  }>;
  ticket_created?: boolean;
  ticket_number?: string;
}

export interface RuleQuestion {
  id: number;
  parent_id: number | null;
  question_text: string;
  option_key: string | null;
  answer_text: string | null;
  is_active: boolean;
  display_order: number;
}

export interface RuleChatResponse {
  session_id: string;
  records: Array<{
    message_type: string;
    message_text: string;
    question_id?: number;
  }>;
  nextQuestions?: RuleQuestion[];
  questions?: RuleQuestion[];
}

// ── API Methods ──────────────────────────────────────────────────
export const chatbotApi = {
  /** Smart search: KB + Q&A matching - server returns { success, data } */
  askQuestion: (message: string, sessionId: string, email?: string) =>
    apiPost<{ success: boolean; data: AskResponse }>('/api/chatbot/ask', { message, session_id: sessionId, email }),

  /** Get root questions - server returns { questions: [...] } directly */
  getRootQuestions: () =>
    apiGet<{ questions: RuleQuestion[] }>('/api/rule-questions'),

  /** Send a message/question - server returns { session_id, records, nextQuestions } directly */
  sendMessage: (sessionId: string, questionId?: number, messageText?: string) =>
    apiPost<RuleChatResponse>('/api/rule-chat/message', {
      session_id: sessionId,
      question_id: questionId,
      message_text: messageText,
    }),

  /** Create a support ticket - server returns { success, data } */
  createTicket: (sessionId: string, email: string, type: string, description: string, subject?: string) =>
    apiPost<{ success: boolean; data: TicketData }>('/api/chatbot/ticket', { session_id: sessionId, email, type, description, subject }),
};
